import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const p = PRICING_2026.paint;

export const articleHowMuchPaintNeed: Article = {
  slug: "how-much-paint-do-you-need-per-room",
  title: "How Much Paint Do You Need Per Room? The Calculation and What Changes It",
  description: "One gallon of paint covers approximately 400 square feet of smooth wall with one coat. Texture, porosity, and dark-to-light color changes reduce that significantly. Here's the formula and what adjusts it.",
  category: "Paint",
  tags: ["Paint", "DIY", "Materials", "Home Decorating"],
  createdAt: "June 25, 2026",
  updatedAt: "June 29, 2026",
  lastVerified: "June 29, 2026",
  readTime: "5 min read",
  keyTakeaway: `A standard gallon of paint covers approximately ${p.coverageSqFtPerGallon} square feet of wall area. To estimate the paint you need, calculate the perimeter of the room, multiply by the ceiling height, subtract window and door areas, and divide by ${p.coverageSqFtPerGallon}. Always plan for two coats.`,
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "Paint Quality Institute - Painting Coverage and Sheen Estimations",
      url: "https://www.paintquality.com"
    },
    {
      name: "Sherwin-Williams - Interior Paint Coverage and Calculations",
      url: "https://www.sherwin-williams.com"
    }
  ],
  keyMetrics: [
    { label: "Standard Coverage / Gal", value: `${p.coverageSqFtPerGallon} sq. ft.` },
    { label: "Porous Surface Coverage", value: "300 sq. ft." },
    { label: "Standard Door Deduction", value: "21 sq. ft." },
    { label: "Standard Window Deduction", value: "15 sq. ft." }
  ],
  faqs: [
    {
      question: "Do I need to prime before painting?",
      answer: "Yes, if you're painting new drywall, covering dark colors, or painting over stains. Primer seals porous surfaces and ensures uniform color. Skip primer only if you're painting a light color over a similar existing color in good condition."
    },
    {
      question: "Should I buy extra paint for touch-ups?",
      answer: "Yes. Buy 10-15% more than your calculation requires. You'll need it for future touch-ups, and color matching years later can be difficult. Store leftover paint in an airtight container away from extreme temperatures."
    }
  ],
  content: `
    <p>
      Paint coverage is listed on every can, typically ${p.coverageSqFtPerGallon} square feet per gallon for smooth surfaces, one coat. The actual coverage in a real room is usually lower, for three reasons: surface texture increases total surface area; two coats are required for most color changes; and new or porous surfaces absorb more paint than the coverage rate assumes.
    </p>
    <p>
      The calculation isn't complicated, but where most homeowners go wrong is using the manufacturer's single-coat coverage figure and not adjusting for two coats and surface conditions. This guide walks through the full calculation.
    </p>

    <div class="my-6 p-4 rounded-lg bg-surface border border-border-custom">
      <p class="font-bold text-foreground m-0 mb-2">The Short Version</p>
      <p class="text-sm text-text-muted m-0">
        Formula: (Room perimeter × ceiling height) minus window and door areas, divided by ${p.coverageSqFtPerGallon}, then double for two coats. A 12×12 room with 8-foot ceilings and one door plus two windows needs approximately 2 gallons for two coats (you can run this instantly on our <a href="/calculators/paint/" class="text-[var(--accent)] hover:underline font-semibold">paint calculator</a>). New drywall and textured surfaces use significantly more paint than smooth, previously painted walls.
      </p>
      <p class="text-xs text-text-muted mt-2 m-0">
        <em>Coverage estimates per Paint Quality Institute and Sherwin-Williams interior paint guidelines.</em>
      </p>
    </div>

    <h2>What this article covers:</h2>
    <ul>
      <li>The wall area formula and how to use it</li>
      <li>When to subtract windows and doors vs. when to skip the step</li>
      <li>Porous and textured surfaces: how much more paint they require</li>
      <li>When primer is required (and why it reduces total paint cost)</li>
    </ul>
    <h2>The Basic Paint Formula</h2>
    <p>
      Start with the perimeter of your room. Add up all four wall lengths, then multiply by the ceiling height. This gives you the total wall area before subtracting openings.
    </p>
    <p class="font-mono bg-surface-muted/50 p-4 border rounded border-border text-sm">
      Wall Area = (Length + Width) × 2 × Ceiling Height
    </p>
    <p>
      For a 12×12 room with 8-foot ceilings: (12 + 12) × 2 × 8 = 384 square feet of wall area.
    </p>
    <p>
      Now subtract windows and doors. A standard interior door is 21 square feet (3×7). A typical window is 15 square feet (3×5). If your room has one door and two windows, subtract 51 square feet: 384 - 51 = 333 square feet of paintable surface.
    </p>

    <div class="my-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
      <p class="font-bold text-foreground m-0 mb-2">Meazora Tip</p>
      <p class="text-sm text-text-muted m-0">
        Don't subtract windows and doors if you want a quick estimate. Instead, calculate total wall area and add 10% buffer. This compensates for waste, touch-ups, and the fact that most people apply paint thicker than the manufacturer's coverage rating assumes. (For exterior projects, which involve completely different coverage factors and prep requirements, read our <a href="/articles/cost-to-paint-house-exterior/" class="text-[var(--accent)] hover:underline font-semibold">exterior house painting cost guide</a>).
      </p>
    </div>

    <h2>Dry Drywall and Raw Plaster: The Thirsty Wall Problem</h2>
    <p>
      New drywall is essentially paper and gypsum dust, which acts like a massive sponge. If you roll paint directly onto raw drywall or sanded joint compound, it sucks the moisture out of the paint immediately. This rapid absorption ruins the finish, causing splotchy sheens (known as flashing) and weak adhesion that leads to peeling down the road.
    </p>
    <p>
      Applying a dedicated PVA (polyvinyl acetate) drywall primer is crucial. PVA primer seals the pores, creating a uniform, non-porous foundation. Skimping on primer doesn't save money; you'll simply end up needing three or four coats of expensive color paint instead of two.
    </p>
    <p>
      Plaster walls are even more demanding. Fresh plaster is highly alkaline and can take weeks to cure. If you paint too soon, the high pH will chemically break down latex binders, causing the paint to blister and flake. An alkali-resistant acrylic primer is a necessity here.
    </p>

    <!-- CALL TO ACTION: Paint Calculator -->
    <div class="my-8 p-6 rounded-xl border border-border bg-surface-muted/30 flex flex-col sm:flex-row items-center gap-6 justify-between">
      <div class="flex-1">
        <h3 class="text-lg font-bold text-foreground m-0 mb-1">Skip the Paint Math</h3>
        <p class="text-sm text-text-muted m-0">Input your wall dimensions, doors, and windows to get an instant gallon estimate for one or two coats using our free Paint Calculator.</p>
      </div>
      <a href="/calculators/paint/" class="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[var(--accent)] text-white hover:bg-[var(--accent-dark)] font-mono font-bold text-xs uppercase tracking-wider transition-colors shrink-0 text-center whitespace-nowrap w-full sm:w-auto min-w-[180px]">Go to Paint Calculator</a>
    </div>

    <h2>Textured Walls: The Hidden Paint Eaters</h2>
    <p>
      Smooth drywall yields the standard ${p.coverageSqFtPerGallon} square feet per gallon. But textured walls, orange peel, knockdown, or heavy plaster, can cut coverage by 30-50%. The peaks and valleys of texture create vastly more surface area than a flat wall.
    </p>
    <p>
      If your walls have heavy texture, plan on 250-300 square feet per gallon instead of 400. It's better to have leftover paint than to run short and risk a visible color mismatch when buying a second batch.
    </p>

    <h2>Example: 12×12 Bedroom Calculation</h2>
    <p>
      Let's walk through a real example:
    </p>
    <ul>
      <li>Room: 12×12 feet with 8-foot ceilings</li>
      <li>Walls: (12 + 12) × 2 × 8 = 384 sq ft</li>
      <li>Subtract: 1 door (21 sq ft) + 2 windows (30 sq ft) = 51 sq ft</li>
      <li>Paintable area: 384 - 51 = 333 sq ft</li>
      <li>Two coats: 333 × 2 = 666 sq ft total</li>
      <li>Gallons needed: 666 ÷ 400 = 1.67 gallons (round up to 2 gallons)</li>
    </ul>

    <p>
      Always buy all cans from the same manufacturer's batch. Minor pigment variances between production batches can produce visible color differences on large wall surfaces, particularly noticeable in flat and matte finishes. When purchasing multiple cans, confirm the batch number printed on the lid before leaving the store.
    </p>
    <p>
      Use our <a href="/calculators/paint/" class="text-accent underline decoration-accent/30 hover:decoration-accent/60 transition-colors">Paint Calculator</a> to estimate gallon counts for your room dimensions, finish type, and number of coats.
    </p>
  `
};